import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'pink';
  });

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved !== null ? JSON.parse(saved) : true; // По умолчанию темная тема
  });

  const [layout, setLayout] = useState(() => {
    const saved = localStorage.getItem('layout');
    const defaultLayout = {
      tabs: [
        { id: 'dashboard', label: 'Dashboard', icon: '📊', enabled: true },
        { id: 'players', label: 'Игроки', icon: '👥', enabled: true },
        { id: 'punishments', label: 'Наказания', icon: '⚖️', enabled: true },
        { id: 'active-bans', label: 'Баны', icon: '🚫', enabled: true },
        { id: 'reports', label: 'Жалобы', icon: '🚨', enabled: true },
        { id: 'commands', label: 'Команды', icon: '⌨️', enabled: true },
        { id: 'pvplogs', label: 'PvP Логи', icon: '⚔️', enabled: true },
        { id: 'staff', label: 'Стафф', icon: '👑', enabled: true },
        { id: 'tags', label: 'Теги', icon: '🏷️', enabled: true },
        { id: 'map', label: '3D Карта', icon: '🗺️', enabled: true },
        { id: 'observation', label: 'Наблюдение', icon: '👁️', enabled: true }
      ]
    };
    
    if (saved) {
      const parsedLayout = JSON.parse(saved);
      // Проверяем, есть ли вкладка active-bans в сохраненных настройках
      const hasActiveBans = parsedLayout.tabs?.some(tab => tab.id === 'active-bans');
      
      if (!hasActiveBans) {
        // Добавляем вкладку active-bans после punishments
        const newTabs = [...parsedLayout.tabs];
        const punishmentsIndex = newTabs.findIndex(tab => tab.id === 'punishments');
        if (punishmentsIndex !== -1) {
          newTabs.splice(punishmentsIndex + 1, 0, { id: 'active-bans', label: 'Баны', icon: '🚫', enabled: true });
        } else {
          newTabs.push({ id: 'active-bans', label: 'Баны', icon: '🚫', enabled: true });
        }
        
        const updatedLayout = { ...parsedLayout, tabs: newTabs };
        localStorage.setItem('layout', JSON.stringify(updatedLayout));
        return updatedLayout;
      }
      
      return parsedLayout;
    }
    
    return defaultLayout;
  });

  const [customColor, setCustomColor] = useState(() => {
    const saved = localStorage.getItem('customColor');
    return saved || '#dc255a';
  });

  const themes = {
    pink: {
      primary: '#dc255a',
      primaryHover: '#f8637e',
      primaryAlpha: 'rgba(220,37,90,0.49)',
      primaryPennyAlpha: 'rgba(220,37,90,0.29)',
      accent: '#f8637e',
      name: 'Розовая'
    },
    blue: {
      primary: '#3b82f6',
      primaryHover: '#60a5fa',
      primaryAlpha: 'rgba(59,130,246,0.49)',
      primaryPennyAlpha: 'rgba(59,130,246,0.29)',
      accent: '#60a5fa',
      name: 'Синяя'
    },
    green: {
      primary: '#10b981',
      primaryHover: '#34d399',
      primaryAlpha: 'rgba(16,185,129,0.49)',
      primaryPennyAlpha: 'rgba(16,185,129,0.29)',
      accent: '#34d399',
      name: 'Зеленая'
    },
    purple: {
      primary: '#8b5cf6',
      primaryHover: '#a78bfa',
      primaryAlpha: 'rgba(139,92,246,0.49)',
      primaryPennyAlpha: 'rgba(139,92,246,0.29)',
      accent: '#a78bfa',
      name: 'Фиолетовая'
    },
    dark: {
        primary: '#1f2937',
        primaryHover: '#374151',
        primaryAlpha: 'rgba(31,41,55,0.49)',
        primaryPennyAlpha: 'rgba(31,41,55,0.29)',
        accent: '#374151',
        name: 'Темная'
    },
    custom: {
      primary: '#000000',
      primaryHover: '#333333',
      primaryAlpha: 'rgba(0,0,0,0.49)',
      primaryPennyAlpha: 'rgba(0,0,0,0.29)',
      accent: '#333333',
      name: 'Своя'
    }
  };

  const updateTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    // Обновляем CSS переменные
    const root = document.documentElement;
    let themeColors = themes[newTheme];
    
    // Если выбрана пользовательская тема, используем сохраненный цвет
    if (newTheme === 'custom') {
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : null;
      };
      
      const rgb = hexToRgb(customColor);
      if (rgb) {
        const darkerColor = `#${Math.max(0, rgb.r - 51).toString(16).padStart(2, '0')}${Math.max(0, rgb.g - 51).toString(16).padStart(2, '0')}${Math.max(0, rgb.b - 51).toString(16).padStart(2, '0')}`;
        themeColors = {
          primary: customColor,
          primaryHover: darkerColor,
          primaryAlpha: `rgba(${rgb.r},${rgb.g},${rgb.b},0.49)`,
          primaryPennyAlpha: `rgba(${rgb.r},${rgb.g},${rgb.b},0.29)`,
          accent: darkerColor,
          name: 'Своя'
        };
      }
    }
    
    root.style.setProperty('--color-primary', themeColors.primary);
    root.style.setProperty('--color-primary-hover', themeColors.primaryHover);
    root.style.setProperty('--color-primary-alpha', themeColors.primaryAlpha);
    root.style.setProperty('--color-primary-penny-alpha', themeColors.primaryPennyAlpha);
    root.style.setProperty('--color-accent', themeColors.accent);
  };

  const updateCustomColor = (newColor) => {
    setCustomColor(newColor);
    localStorage.setItem('customColor', newColor);
    
    // Если текущая тема - пользовательская, обновляем её
    if (theme === 'custom') {
      updateTheme('custom');
    }
  };

  const selectCustomTheme = () => {
    updateTheme('custom');
  };

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', JSON.stringify(newDarkMode));
    
    // Применяем класс к body для переключения темной/светлой темы
    if (newDarkMode) {
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.body.classList.add('light');
      document.body.classList.remove('dark');
    }
  };

  const updateLayout = (newLayout) => {
    setLayout(newLayout);
    localStorage.setItem('layout', JSON.stringify(newLayout));
  };

  const toggleTab = (tabId) => {
    const newTabs = layout.tabs.map(tab => 
      tab.id === tabId ? { ...tab, enabled: !tab.enabled } : tab
    );
    updateLayout({ ...layout, tabs: newTabs });
  };

  const reorderTabs = (fromIndex, toIndex) => {
    const newTabs = [...layout.tabs];
    const [movedTab] = newTabs.splice(fromIndex, 1);
    newTabs.splice(toIndex, 0, movedTab);
    updateLayout({ ...layout, tabs: newTabs });
  };

  useEffect(() => {
    updateTheme(theme);
    
    // Инициализируем темную/светлую тему
    if (isDarkMode) {
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.body.classList.add('light');
      document.body.classList.remove('dark');
    }
  }, [theme, customColor]);

  const value = {
    theme,
    themes,
    currentTheme: themes[theme],
    updateTheme,
    customColor,
    updateCustomColor,
    selectCustomTheme,
    isDarkMode,
    toggleDarkMode,
    layout,
    updateLayout,
    toggleTab,
    reorderTabs,
    enabledTabs: layout.tabs.filter(tab => tab.enabled)
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 